import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import type {
  BillingCycleHorizon,
  ExpenseCategory,
  ExpenseSource,
  PaymentMethodType,
  ReceiptDocumentType,
  ReceiptExtractionResult,
  SolTemporalKpiSnapshot,
} from '@economi/domain-contracts';
import {
  classifyExpenseHorizonForCardInCycle,
  computeMultiCycleCardSummary,
  EXPENSE_CATEGORY_LABELS,
  formatSolesFromCents,
  isDateWithinPeriod,
  matchCreditCardByLastFour,
  resolveCardBillingCloseDateForCycle,
  simulateExpenseImpact,
  solesToCents,
  type FinancialCycleState,
} from '../../services/solImpactEngine';
import { scanUploadedReceiptViaApi } from '../../services/apiClient';
import { InfoPopover } from '../InfoPopover/InfoPopover';
import styles from './ReceiptScannerView.module.css';

export const PENDING_RECEIPT_STORAGE_KEY =
  'economi_pending_receipt_validation_v2';

const DOCUMENT_TYPE_LABELS: Record<ReceiptDocumentType, string> = {
  BOLETA_ELECTRONICA: '🧾 Boleta Electrónica SUNAT',
  FACTURA_ELECTRONICA: '🏢 Factura Electrónica SUNAT',
  TICKET_POS: '📠 Ticket / Voucher POS',
  YAPE_PLIN_VOUCHER: '📲 Voucher Yape / Plin / Transferencia',
  BANK_APP_SCREENSHOT: '📱 Captura de App Bancaria (Consumo Tarjeta)',
  UNKNOWN: '📄 Otro Comprobante',
};

interface PersistedPendingReceiptState {
  readonly status: 'SCANNING_WITH_AI' | 'AWAITING_USER_VALIDATION';
  readonly uploadedPreviewUrl: string;
  readonly fileName: string;
  readonly mimeType: string;
  readonly extraction: ReceiptExtractionResult | null;
  readonly editableAmountSoles: number;
  readonly editableIssuedDateIso?: string;
  readonly editableMerchant?: string;
  readonly editableRuc?: string;
  readonly editableCategory?: ExpenseCategory;
  readonly editableDocumentType?: ReceiptDocumentType;
  readonly selectedCardId?: string;
}

function readPendingReceiptFromSession(): PersistedPendingReceiptState | null {
  try {
    const raw = window.sessionStorage.getItem(PENDING_RECEIPT_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PersistedPendingReceiptState;
  } catch {
    return null;
  }
}

function writePendingReceiptToSession(
  state: PersistedPendingReceiptState | null
): void {
  try {
    if (!state) {
      window.sessionStorage.removeItem(PENDING_RECEIPT_STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(
        PENDING_RECEIPT_STORAGE_KEY,
        JSON.stringify(state)
      );
    }
  } catch {
    // Ignore storage quota errors
  }
}

export interface ExternalCapturedCameraFile {
  readonly file: File;
  readonly id: number;
}

interface ReceiptScannerViewProps {
  readonly cycleState: FinancialCycleState;
  readonly snapshot: SolTemporalKpiSnapshot;
  readonly externalCameraFile?: ExternalCapturedCameraFile | null;
  readonly onCancel?: () => void;
  readonly onConfirmScannedExpense: (params: {
    amountSoles: number;
    merchantName: string;
    issuerRuc?: string | null;
    receiptNumber?: string | null;
    category: ExpenseCategory;
    description: string;
    source: ExpenseSource;
    r2ReceiptKey: string;
    receiptImageDataUrl?: string | null;
    occurredAtIso: string;
    paymentMethod: PaymentMethodType;
    creditCardId: string | null;
    cardLastFourDigits?: string | null;
    billingCycleHorizon: BillingCycleHorizon;
    lineItems?: ReceiptExtractionResult['lineItems'];
  }) => void;
}

/**
 * Normaliza y comprime fotos tomadas con la cámara del celular (ej. 12MP–48MP / 8MB)
 * a un JPEG nítido de máximo 1280px (~180KB) para envío rápido a Gemini Vision AI y Cloudflare R2.
 */
async function compressReceiptFileToDataUrl(
  file: File
): Promise<{ dataUrl: string; fileName: string; mimeType: string }> {
  const safeBaseName =
    file.name && file.name.trim().length > 0
      ? file.name.replace(/\.[^.]+$/, '')
      : `camara-boleta-${Date.now()}`;

  return new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      try {
        const maxDimension = 1280;
        let width = img.naturalWidth || img.width || 1080;
        let height = img.naturalHeight || img.height || 1280;

        if (width > maxDimension || height > maxDimension) {
          if (width >= height) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          } else {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height);
          const jpegDataUrl = canvas.toDataURL('image/jpeg', 0.84);
          URL.revokeObjectURL(objectUrl);
          resolve({
            dataUrl: jpegDataUrl,
            fileName: `${safeBaseName}.jpg`,
            mimeType: 'image/jpeg',
          });
          return;
        }
      } catch {
        // Fallback a FileReader abajo si canvas falla
      }

      URL.revokeObjectURL(objectUrl);
      const reader = new FileReader();
      reader.onload = () => {
        resolve({
          dataUrl: typeof reader.result === 'string' ? reader.result : '',
          fileName: file.name || `${safeBaseName}.jpg`,
          mimeType: file.type || 'image/jpeg',
        });
      };
      reader.readAsDataURL(file);
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      const reader = new FileReader();
      reader.onload = () => {
        resolve({
          dataUrl: typeof reader.result === 'string' ? reader.result : '',
          fileName: file.name || `${safeBaseName}.jpg`,
          mimeType: file.type || 'image/jpeg',
        });
      };
      reader.readAsDataURL(file);
    };

    img.src = objectUrl;
  });
}

export function ReceiptScannerView({
  cycleState,
  snapshot,
  externalCameraFile = null,
  onCancel,
  onConfirmScannedExpense,
}: ReceiptScannerViewProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const lastProcessedExternalIdRef = useRef<number>(0);
  const resumedSessionScanRef = useRef<boolean>(false);

  const multiCycle = useMemo(
    () => computeMultiCycleCardSummary(cycleState, snapshot),
    [cycleState, snapshot]
  );

  const initialPersisted = readPendingReceiptFromSession();

  const [extraction, setExtraction] = useState<ReceiptExtractionResult | null>(
    initialPersisted?.extraction ?? null
  );
  const [uploadedPreviewUrl, setUploadedPreviewUrl] = useState<string | null>(
    initialPersisted?.uploadedPreviewUrl ?? null
  );
  const [editableAmountSoles, setEditableAmountSoles] = useState<number>(
    initialPersisted?.editableAmountSoles ?? 0
  );
  const [editableAmountInput, setEditableAmountInput] = useState<string>(() =>
    initialPersisted?.editableAmountSoles
      ? String(initialPersisted.editableAmountSoles)
      : ''
  );
  const [editableIssuedDateIso, setEditableIssuedDateIso] = useState<string>(
    initialPersisted?.editableIssuedDateIso ??
      initialPersisted?.extraction?.issuedAtIso?.slice(0, 10) ??
      cycleState.referenceDateIso.slice(0, 10)
  );
  const [editableMerchant, setEditableMerchant] = useState<string>(
    initialPersisted?.editableMerchant ??
      initialPersisted?.extraction?.merchantName ??
      ''
  );
  const [editableRuc, setEditableRuc] = useState<string>(
    initialPersisted?.editableRuc ?? initialPersisted?.extraction?.issuerRuc ?? ''
  );
  const [editableCategory, setEditableCategory] = useState<ExpenseCategory>(
    initialPersisted?.editableCategory ??
      initialPersisted?.extraction?.suggestedCategory ??
      'OTHER'
  );
  const [editableDocumentType, setEditableDocumentType] =
    useState<ReceiptDocumentType>(
      initialPersisted?.editableDocumentType ??
        initialPersisted?.extraction?.documentType ??
        'BOLETA_ELECTRONICA'
    );
  const [selectedCardId, setSelectedCardId] = useState<string>(() => {
    if (initialPersisted?.selectedCardId !== undefined) {
      return initialPersisted.selectedCardId;
    }
    if (initialPersisted?.extraction?.cardLastFourDigits) {
      const matched = matchCreditCardByLastFour(
        cycleState.creditCards,
        initialPersisted.extraction.cardLastFourDigits
      );
      return matched ? matched.id : '';
    }
    return '';
  });
  const [isScanning, setIsScanning] = useState<boolean>(
    initialPersisted?.status === 'SCANNING_WITH_AI'
  );
  const [scanError, setScanError] = useState<string | null>(null);

  const syncEditableFieldsToSession = (patch: {
    editableAmountSoles?: number;
    editableIssuedDateIso?: string;
    editableMerchant?: string;
    editableRuc?: string;
    editableCategory?: ExpenseCategory;
    editableDocumentType?: ReceiptDocumentType;
    selectedCardId?: string;
  }) => {
    const existing = readPendingReceiptFromSession();
    if (existing) {
      writePendingReceiptToSession({
        ...existing,
        ...patch,
      });
    }
  };

  const runAiExtractionOnDataUrl = async (params: {
    dataUrl: string;
    fileName: string;
    mimeType: string;
  }) => {
    setScanError(null);
    setIsScanning(true);
    setUploadedPreviewUrl(params.dataUrl);

    writePendingReceiptToSession({
      status: 'SCANNING_WITH_AI',
      uploadedPreviewUrl: params.dataUrl,
      fileName: params.fileName,
      mimeType: params.mimeType,
      extraction: null,
      editableAmountSoles: 0,
      editableDocumentType,
      selectedCardId,
    });

    const response = await scanUploadedReceiptViaApi({
      fileName: params.fileName,
      mimeType: params.mimeType,
      imageBase64: params.dataUrl,
      documentTypeHint: editableDocumentType,
    });

    setIsScanning(false);

    if (response.ok) {
      const ext = response.data.extraction;
      const extractedDateIso =
        ext.issuedAtIso?.slice(0, 10) || cycleState.referenceDateIso.slice(0, 10);
      const resolvedDocType: ReceiptDocumentType =
        editableDocumentType === 'BANK_APP_SCREENSHOT'
          ? 'BANK_APP_SCREENSHOT'
          : ext.documentType;

      const matchedByDigits = matchCreditCardByLastFour(
        cycleState.creditCards,
        ext.cardLastFourDigits
      );
      const matchedByServerId = response.data.matchedCreditCardId
        ? (cycleState.creditCards ?? []).find(
            (c) => c.id === response.data.matchedCreditCardId && c.isActive !== false
          ) ?? null
        : null;
      const matchedCard = matchedByDigits ?? matchedByServerId;
      const nextSelectedCardId = matchedCard ? matchedCard.id : '';

      setExtraction(ext);
      setEditableAmountSoles(ext.totalAmountSoles);
      setEditableAmountInput(String(ext.totalAmountSoles));
      setEditableIssuedDateIso(extractedDateIso);
      setEditableMerchant(ext.merchantName);
      setEditableRuc(ext.issuerRuc ?? '');
      setEditableCategory(ext.suggestedCategory);
      setEditableDocumentType(resolvedDocType);
      setSelectedCardId(nextSelectedCardId);

      writePendingReceiptToSession({
        status: 'AWAITING_USER_VALIDATION',
        uploadedPreviewUrl: params.dataUrl,
        fileName: params.fileName,
        mimeType: params.mimeType,
        extraction: ext,
        editableAmountSoles: ext.totalAmountSoles,
        editableIssuedDateIso: extractedDateIso,
        editableMerchant: ext.merchantName,
        editableRuc: ext.issuerRuc ?? '',
        editableCategory: ext.suggestedCategory,
        editableDocumentType: resolvedDocType,
        selectedCardId: nextSelectedCardId,
      });
    } else {
      setScanError(response.error);
    }
  };

  const processCapturedOrUploadedFile = async (file: File) => {
    setScanError(null);
    setIsScanning(true);

    const compressed = await compressReceiptFileToDataUrl(file);
    if (!compressed.dataUrl) {
      setIsScanning(false);
      setScanError('No se pudo leer la imagen tomada. Intenta nuevamente.');
      return;
    }

    await runAiExtractionOnDataUrl({
      dataUrl: compressed.dataUrl,
      fileName: compressed.fileName,
      mimeType: compressed.mimeType,
    });
  };

  useEffect(() => {
    if (resumedSessionScanRef.current) return;
    resumedSessionScanRef.current = true;

    const pending = readPendingReceiptFromSession();
    if (
      pending &&
      pending.status === 'SCANNING_WITH_AI' &&
      pending.uploadedPreviewUrl &&
      !externalCameraFile
    ) {
      void runAiExtractionOnDataUrl({
        dataUrl: pending.uploadedPreviewUrl,
        fileName: pending.fileName,
        mimeType: pending.mimeType,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      externalCameraFile &&
      externalCameraFile.id !== lastProcessedExternalIdRef.current
    ) {
      lastProcessedExternalIdRef.current = externalCameraFile.id;
      void processCapturedOrUploadedFile(externalCameraFile.file);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalCameraFile]);

  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    void processCapturedOrUploadedFile(file);
  };

  const impact = simulateExpenseImpact(snapshot, editableAmountSoles);
  const isWithinActivePeriod = isDateWithinPeriod(
    editableIssuedDateIso,
    snapshot.startDateIso,
    snapshot.renewalDateIso
  );

  const selectedCardSummary = useMemo(
    () =>
      selectedCardId
        ? multiCycle.cards.find((c) => c.card.id === selectedCardId) ?? null
        : null,
    [multiCycle.cards, selectedCardId]
  );

  const selectedCardCloseDateIso = useMemo(() => {
    if (!selectedCardSummary) return null;
    return resolveCardBillingCloseDateForCycle(
      selectedCardSummary.card.billingCloseDayOfMonth,
      cycleState.startDateIso,
      cycleState.renewalDateIso
    );
  }, [selectedCardSummary, cycleState.startDateIso, cycleState.renewalDateIso]);

  const classifiedHorizon: BillingCycleHorizon = useMemo(
    () =>
      classifyExpenseHorizonForCardInCycle(
        cycleState,
        editableIssuedDateIso,
        selectedCardId || null
      ),
    [cycleState, editableIssuedDateIso, selectedCardId]
  );

  const alternativeOpenCard = useMemo(
    () =>
      multiCycle.cards.find(
        (c) =>
          c.card.id !== selectedCardId &&
          c.isBillingOpenToday &&
          !c.hasReachedLimitInCurrentCycle
      ) ?? null,
    [multiCycle.cards, selectedCardId]
  );

  const aiDetectedLastFour = extraction?.cardLastFourDigits?.trim() || null;
  const aiMatchedCard = useMemo(
    () => matchCreditCardByLastFour(cycleState.creditCards, aiDetectedLastFour),
    [cycleState.creditCards, aiDetectedLastFour]
  );

  const handleConfirmExpense = () => {
    if (!extraction || editableAmountSoles <= 0 || isScanning) return;

    const finalMerchant = editableMerchant.trim() || extraction.merchantName;
    const finalRuc = editableRuc.trim() || extraction.issuerRuc || null;
    const finalIssuedDateIso =
      editableIssuedDateIso.trim().slice(0, 10) ||
      extraction.issuedAtIso.slice(0, 10) ||
      cycleState.referenceDateIso.slice(0, 10);

    const hasSelectedCard = Boolean(selectedCardId && selectedCardSummary);
    const docBadge = DOCUMENT_TYPE_LABELS[editableDocumentType];
    const effectiveLastFour =
      selectedCardSummary?.card.lastFourDigits?.trim() ||
      extraction.cardLastFourDigits?.trim() ||
      null;

    const finalCents = Math.max(1, solesToCents(editableAmountSoles));
    const rawItems = extraction.lineItems ?? [];
    const reconciledLineItems =
      rawItems.length === 0
        ? [
            {
              description: finalMerchant,
              quantity: 1,
              totalCents: finalCents,
            },
          ]
        : rawItems.length === 1
          ? [
              {
                ...rawItems[0]!,
                totalCents: finalCents,
              },
            ]
          : (() => {
              const prevSum = rawItems.reduce(
                (acc, li) => acc + li.totalCents,
                0
              );
              if (prevSum <= 0 || prevSum === finalCents) return rawItems;
              let allocated = 0;
              return rawItems.map((li, idx, arr) => {
                if (idx === arr.length - 1) {
                  return {
                    ...li,
                    totalCents: Math.max(1, finalCents - allocated),
                  };
                }
                const scaled = Math.max(
                  1,
                  Math.round((li.totalCents * finalCents) / prevSum)
                );
                allocated += scaled;
                return {
                  ...li,
                  totalCents: scaled,
                };
              });
            })();

    writePendingReceiptToSession(null);
    onConfirmScannedExpense({
      amountSoles: editableAmountSoles,
      merchantName: finalMerchant,
      issuerRuc: finalRuc,
      receiptNumber: extraction.receiptNumber,
      category: editableCategory,
      description: `${docBadge} ${extraction.receiptNumber ?? 'S/N'} · RUC ${
        finalRuc ?? 'N/A'
      }${
        selectedCardSummary
          ? ` · Pagado con ${selectedCardSummary.card.alias}${
              selectedCardSummary.card.lastFourDigits
                ? ` (*${selectedCardSummary.card.lastFourDigits})`
                : ''
            }`
          : ''
      }`,
      source: 'RECEIPT_OCR',
      r2ReceiptKey: extraction.r2ObjectKey,
      receiptImageDataUrl: uploadedPreviewUrl ?? null,
      occurredAtIso: finalIssuedDateIso,
      paymentMethod: 'CREDIT_CARD',
      creditCardId: hasSelectedCard ? selectedCardSummary!.card.id : null,
      cardLastFourDigits: effectiveLastFour,
      billingCycleHorizon: hasSelectedCard ? classifiedHorizon : 'CURRENT_CYCLE',
      lineItems: reconciledLineItems,
    });
  };

  const handleCancelAndReturn = () => {
    writePendingReceiptToSession(null);
    setExtraction(null);
    setUploadedPreviewUrl(null);
    setEditableAmountSoles(0);
    setEditableAmountInput('');
    onCancel?.();
  };

  return (
    <div className={styles.validationContainer}>
      <div className={styles.card}>
        {/* 1. Cabecera Compacta: "📷 Escáner IA de Boletas y Vouchers" + <InfoPopover /> */}
        <div className={styles.header}>
          <div className={styles.titleWithInfo}>
            <h2 className={styles.title}>
              📷 Escáner IA de Boletas y Vouchers
            </h2>
            <InfoPopover title="Lectura Inteligente de Comprobantes">
              Sube o toma foto de tu boleta SUNAT, voucher POS o captura de consumo bancario/Yape/Plin. La IA extrae RUC, fecha, monto y los últimos 4 dígitos de tu tarjeta.
            </InfoPopover>
          </div>

          <span className={styles.confidenceTag}>
            {isScanning
              ? '⏳ Analizando...'
              : extraction
                ? `IA ${extraction.confidencePercent.toFixed(0)}%`
                : 'Listo'}
          </span>
        </div>

        {/* Selector en 1 toque: Tarjeta de Crédito + Detección IA de 4 dígitos */}
        <div className={styles.paymentSelectorBox}>
          <div className={styles.paymentSelectorHeader}>
            <span
              className={styles.paymentSelectorLabel}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
            >
              💳 Tarjeta de Crédito
              <InfoPopover title="Horizonte de Facturación">
                Según la fecha de emisión y el día de facturación de la tarjeta elegida, el gasto se asigna automáticamente al Ciclo Actual o al Próximo Ciclo.
              </InfoPopover>
            </span>
            <select
              value={editableDocumentType}
              onChange={(e) => {
                const nextType = e.target.value as ReceiptDocumentType;
                setEditableDocumentType(nextType);
                syncEditableFieldsToSession({ editableDocumentType: nextType });
              }}
              className={styles.docTypeSelect}
              aria-label="Tipo de documento o captura"
            >
              <option value="BOLETA_ELECTRONICA">
                🧾 Boleta Electrónica SUNAT
              </option>
              <option value="BANK_APP_SCREENSHOT">
                📱 Captura de App Bancaria (BANK_APP_SCREENSHOT)
              </option>
              <option value="YAPE_PLIN_VOUCHER">📲 Voucher Yape / Plin (Con Tarjeta)</option>
              <option value="TICKET_POS">📠 Ticket POS</option>
              <option value="FACTURA_ELECTRONICA">🏢 Factura Electrónica</option>
            </select>
          </div>

          {extraction && (
            aiDetectedLastFour && aiMatchedCard ? (
              <div
                className={styles.aiMatchedCardBanner}
                style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
              >
                <span>
                  🤖 Tarjeta detectada (*{aiDetectedLastFour}):{' '}
                  <strong>{aiMatchedCard.alias}</strong>
                </span>
                <InfoPopover title="Detección Automática por IA">
                  La IA identificó los últimos 4 dígitos (*{aiDetectedLastFour}) en el comprobante y seleccionó automáticamente tu tarjeta {aiMatchedCard.alias}
                  {aiMatchedCard.issuerBank ? ` (${aiMatchedCard.issuerBank})` : ''}.
                </InfoPopover>
              </div>
            ) : (
              <div
                className={styles.aiMissingDigitsBanner}
                style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
              >
                <span>⚠️ Selecciona la tarjeta usada</span>
                <InfoPopover title="Selección Manual de Tarjeta">
                  La IA no detectó los 4 dígitos de tu tarjeta en el comprobante
                  {aiDetectedLastFour
                    ? ` (leído *${aiDetectedLastFour} sin coincidencia en tus tarjetas)`
                    : ' (ej. pago por Yape/Plin con tarjeta)'}. Elige abajo la tarjeta utilizada.
                </InfoPopover>
              </div>
            )
          )}

          <div className={styles.cardSelectRow}>
            <select
              value={selectedCardId}
              onChange={(e) => {
                const nextId = e.target.value;
                setSelectedCardId(nextId);
                syncEditableFieldsToSession({ selectedCardId: nextId });
              }}
              className={styles.cardDropdownSelect}
              aria-label="Seleccionar tarjeta de crédito utilizada"
            >
              <option value="">
                ⚠️ Sin tarjeta asignada — Seleccionar tarjeta
              </option>
              {multiCycle.cards.map((c) => (
                <option key={c.card.id} value={c.card.id}>
                  💳 {c.card.alias} ({c.card.issuerBank}
                  {c.card.lastFourDigits ? ` · *${c.card.lastFourDigits}` : ''}) · Factura día{' '}
                  {c.card.billingCloseDayOfMonth}
                </option>
              ))}
            </select>
          </div>

          {multiCycle.cards.length > 0 && (
            <div className={styles.paymentChipsRow}>
              {multiCycle.cards.map((c) => {
                const isSelected = selectedCardId === c.card.id;
                const isRec = multiCycle.advisor.recommendedCardId === c.card.id;
                return (
                  <button
                    key={c.card.id}
                    type="button"
                    className={`${styles.paymentChipBtn} ${
                      isSelected ? styles.paymentChipBtnActive : ''
                    }`}
                    onClick={() => {
                      setSelectedCardId(c.card.id);
                      syncEditableFieldsToSession({ selectedCardId: c.card.id });
                    }}
                  >
                    <span>
                      💳 {c.card.alias}
                      {c.card.lastFourDigits ? ` (*${c.card.lastFourDigits})` : ''}
                    </span>
                    <span className={styles.paymentChipSubTag}>
                      {isRec
                        ? '⭐ Recomendada'
                        : c.isBillingOpenToday
                          ? `Abierta al ${c.currentCycleBillingCloseDateIso.slice(5)}`
                          : 'Cerrada → Próx. Ciclo'}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Alerta inmediata si la tarjeta seleccionada ya llegó a su tope en el ciclo actual */}
          {selectedCardSummary &&
            selectedCardSummary.hasReachedLimitInCurrentCycle &&
            classifiedHorizon === 'CURRENT_CYCLE' && (
              <div className={styles.cardLimitAlertBanner}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                  ⚠️ <strong>Tope alcanzado en {selectedCardSummary.card.alias}</strong>
                  <InfoPopover title="Alerta de Tope en Tarjeta">
                    Llegaste al tope de gastos con {selectedCardSummary.card.alias} en el ciclo actual ({formatSolesFromCents(selectedCardSummary.currentCycleSpentCents)} / {formatSolesFromCents(selectedCardSummary.effectiveCardLimitCents)}).{' '}
                    {alternativeOpenCard
                      ? `Te recomendamos usar ${alternativeOpenCard.card.alias} (abierta hasta el ${alternativeOpenCard.currentCycleBillingCloseDateIso}).`
                      : 'No tienes otra tarjeta con facturación abierta y saldo disponible en este ciclo.'}
                  </InfoPopover>
                </span>
                {alternativeOpenCard && (
                  <button
                    type="button"
                    className={styles.switchCardAlertBtn}
                    onClick={() => {
                      setSelectedCardId(alternativeOpenCard.card.id);
                      syncEditableFieldsToSession({
                        selectedCardId: alternativeOpenCard.card.id,
                      });
                    }}
                  >
                    Cambiar a 💳 {alternativeOpenCard.card.alias}
                  </button>
                )}
              </div>
            )}
        </div>

        {/* Input oculto para subir archivo / tomar foto */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/*"
          onChange={handleFileUpload}
          className={styles.hiddenInput}
        />

        {/* Botón de subir desde galería/archivo en Vista de Computadora */}
        <div className={styles.desktopOnlyUploadRow}>
          <button
            type="button"
            className={styles.cameraSecondaryBtn}
            onClick={() => fileInputRef.current?.click()}
          >
            📁 Subir Boleta o Captura
          </button>
        </div>

        {isScanning ? (
          <div className={styles.scanningAlertBox}>
            <div className={styles.scanningHeaderRow}>
              {uploadedPreviewUrl && (
                <img
                  src={uploadedPreviewUrl}
                  alt="Miniatura de comprobante"
                  className={styles.miniReceiptThumb}
                />
              )}
              <div>
                <strong>⏳ Analizando comprobante con Vision AI...</strong>
              </div>
            </div>
          </div>
        ) : !extraction ? (
          <div className={styles.emptyScannerBox}>
            <span className={styles.emptyScannerIcon}>📷</span>
            <h3 className={styles.emptyScannerTitle}>
              Sin comprobante cargado
            </h3>
            {scanError && (
              <div className={styles.errorAlertBox}>
                ⚠️ <strong>Error:</strong> {scanError}
              </div>
            )}
            <button
              type="button"
              className={styles.emptyScannerActionBtn}
              onClick={() => fileInputRef.current?.click()}
            >
              📷 Escanear Boleta o Captura
            </button>
          </div>
        ) : (
          <>
            {scanError && (
              <div className={styles.errorAlertBox}>
                ⚠️ <strong>Error de escaneo:</strong> {scanError}
              </div>
            )}

            {/* 2. Barra Superior Protagonista: Miniatura + Monto Extraído + BOTÓN DE APROBAR VISIBLE ARRIBA */}
            <div className={styles.topApprovalCard}>
              <div className={styles.topSummaryRow}>
                <div className={styles.miniThumbWrapper}>
                  {uploadedPreviewUrl ? (
                    <img
                      src={uploadedPreviewUrl}
                      alt="Miniatura de comprobante"
                      className={styles.miniReceiptThumb}
                    />
                  ) : (
                    <div className={styles.miniReceiptPlaceholder}>🧾</div>
                  )}
                </div>

                <div className={styles.topMerchantBlock}>
                  <span className={styles.topMerchantLabel}>
                    {DOCUMENT_TYPE_LABELS[editableDocumentType]} ·{' '}
                    {extraction.receiptNumber ?? 'S/N'}
                  </span>
                  <strong className={styles.topMerchantName}>
                    {editableMerchant || extraction.merchantName}
                  </strong>
                </div>

                <div className={styles.topAmountEditor}>
                  <span className={styles.topAmountCurrency}>S/</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={editableAmountInput}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setEditableAmountInput(raw);
                      const cleaned = raw
                        .trim()
                        .replace(/[^0-9.,]/g, '')
                        .replace(',', '.');
                      const parsed = Number(cleaned);
                      const nextVal =
                        Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
                      setEditableAmountSoles(nextVal);
                      syncEditableFieldsToSession({
                        editableAmountSoles: nextVal,
                      });
                    }}
                    className={styles.topAmountInput}
                    aria-label="Importe total extraído en Soles editable"
                  />
                </div>
              </div>

              {/* Fecha de Emisión Editable + Badge Dinámico de Impacto por Fecha de Facturación de Tarjeta */}
              <div className={styles.issueDateValidationRow}>
                <div className={styles.issueDateFieldGroup}>
                  <label className={styles.issueDateLabel}>
                    📅 Fecha de Emisión / Consumo (extraída por IA o editable)
                    <input
                      type="date"
                      value={editableIssuedDateIso}
                      onChange={(e) => {
                        const nextDate = e.target.value;
                        setEditableIssuedDateIso(nextDate);
                        syncEditableFieldsToSession({
                          editableIssuedDateIso: nextDate,
                        });
                      }}
                      className={styles.issueDateInput}
                    />
                  </label>
                </div>

                {selectedCardSummary && selectedCardCloseDateIso ? (
                  <div
                    className={
                      classifiedHorizon === 'CURRENT_CYCLE'
                        ? styles.periodBadgeActive
                        : styles.periodBadgeNextCycle
                    }
                  >
                    {classifiedHorizon === 'CURRENT_CYCLE'
                      ? `🟢 Impacta: Ciclo Actual (puedes gastar con esta tarjeta hasta el ${selectedCardCloseDateIso})`
                      : `🔵 Impacta: Próximo Ciclo (la facturación cerró el ${selectedCardCloseDateIso} — no reduce tu disponible de este mes)`}
                  </div>
                ) : (
                  <div className={styles.periodBadgeOther}>
                    {isWithinActivePeriod
                      ? '⚠️ Sin tarjeta seleccionada: elige arriba cuál de tus tarjetas de crédito utilizaste para calcular su fecha de facturación (Ciclo Actual vs Próximo Ciclo).'
                      : `📅 Comprobante de otro período (${editableIssuedDateIso.slice(
                          0,
                          7
                        )}) — Selecciona la tarjeta utilizada; se agrupará según su fecha de emisión.`}
                  </div>
                )}
              </div>

              {/* Botón de Aprobar visible inmediatamente arriba */}
              <div className={styles.topActionButtonsRow}>
                <button
                  type="button"
                  className={styles.confirmBtnTop}
                  disabled={editableAmountSoles <= 0}
                  onClick={handleConfirmExpense}
                >
                  ✅ Aprobar y Guardar (
                  {formatSolesFromCents(solesToCents(editableAmountSoles))} ·{' '}
                  {selectedCardSummary
                    ? `💳 ${selectedCardSummary.card.alias}${
                        selectedCardSummary.card.lastFourDigits
                          ? ` (*${selectedCardSummary.card.lastFourDigits})`
                          : ''
                      }`
                    : '⚠️ Sin tarjeta asignada'}
                  )
                </button>

                <button
                  type="button"
                  className={styles.cancelBtnTop}
                  onClick={handleCancelAndReturn}
                  title="Descartar o cancelar"
                >
                  ✕ Descartar
                </button>
              </div>
            </div>

            {/* 3. Detalle de lo Extraído por la IA */}
            <div className={styles.receiptPaper}>
              <div className={styles.metaGrid}>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>
                    EMISOR / COMERCIO (EDITABLE)
                  </span>
                  <input
                    type="text"
                    value={editableMerchant}
                    onChange={(e) => {
                      setEditableMerchant(e.target.value);
                      syncEditableFieldsToSession({
                        editableMerchant: e.target.value,
                      });
                    }}
                    className={styles.metaInlineInput}
                  />
                </div>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>RUC SUNAT (EDITABLE)</span>
                  <input
                    type="text"
                    value={editableRuc}
                    placeholder="No aplica (ej. captura bancaria)"
                    onChange={(e) => {
                      setEditableRuc(e.target.value);
                      syncEditableFieldsToSession({
                        editableRuc: e.target.value,
                      });
                    }}
                    className={styles.metaInlineInput}
                  />
                </div>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>CATEGORÍA SUGERIDA</span>
                  <select
                    value={editableCategory}
                    onChange={(e) => {
                      const nextCat = e.target.value as ExpenseCategory;
                      setEditableCategory(nextCat);
                      syncEditableFieldsToSession({
                        editableCategory: nextCat,
                      });
                    }}
                    className={styles.metaInlineSelect}
                  >
                    {(
                      Object.keys(EXPENSE_CATEGORY_LABELS) as Array<ExpenseCategory>
                    ).map((cat) => (
                      <option key={cat} value={cat}>
                        {EXPENSE_CATEGORY_LABELS[cat]}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>
                    TIPO DE DOCUMENTO / ARCHIVO R2
                  </span>
                  <span className={styles.metaValue}>
                    {DOCUMENT_TYPE_LABELS[editableDocumentType]} ·{' '}
                    {extraction.r2ObjectKey}
                  </span>
                </div>
              </div>

              <div className={styles.itemsList}>
                <span className={styles.metaLabel}>
                  DETALLE EXTRAÍDO DEL COMPROBANTE / CAPTURA
                </span>
                {extraction.lineItems.map((line, idx) => (
                  <div
                    key={`${line.description}-${idx}`}
                    className={styles.itemRow}
                  >
                    <span>
                      {line.quantity}x {line.description}
                    </span>
                    <span>{formatSolesFromCents(line.totalCents)}</span>
                  </div>
                ))}
              </div>

              <div className={styles.impactInlineBar}>
                {selectedCardSummary && classifiedHorizon === 'NEXT_CYCLE' ? (
                  <span>
                    🔵{' '}
                    <strong style={{ color: '#38bdf8' }}>
                      Pasa al Próximo Ciclo:
                    </strong>{' '}
                    Tu disponible del Ciclo Actual se mantiene intacto en{' '}
                    <strong className={styles.impactInlineHighlight}>
                      {formatSolesFromCents(snapshot.remainingCapCents)} (
                      {snapshot.remainingSpendingCapacityPercent.toFixed(1)}%)
                    </strong>
                  </span>
                ) : (
                  <>
                    <span>
                      Capacidad restante tras aprobar:{' '}
                      <strong className={styles.impactInlineHighlight}>
                        {formatSolesFromCents(
                          impact.remainingCapAfterExpenseCents
                        )}{' '}
                        ({impact.newRemainingSpendingCapacityPercent.toFixed(1)}
                        %)
                      </strong>
                    </span>
                    <span>
                      Cuota diaria:{' '}
                      <strong>
                        {formatSolesFromCents(impact.newSafeDailyAllowanceCents)}
                        /día
                      </strong>
                    </span>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
